/**
 * PKCE (Proof Key for Code Exchange) utilities for OAuth 2.0 Authorization Code flow.
 * Uses Web Crypto API for secure code verifier/challenge generation.
 */

function generateRandomString(length: number): string {
  const array = new Uint8Array(length)
  crypto.getRandomValues(array)
  return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('')
}

async function sha256(plain: string): Promise<ArrayBuffer> {
  const encoder = new TextEncoder()
  const data = encoder.encode(plain)
  return crypto.subtle.digest('SHA-256', data)
}

function base64UrlEncode(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte)
  })
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/**
 * Generate a cryptographically random code verifier for PKCE.
 * Produces 64 hex characters (32 bytes).
 */
export function generateCodeVerifier(): string {
  return generateRandomString(32)
}

/**
 * Generate a code challenge from a code verifier using SHA-256 (S256 method).
 */
export async function generateCodeChallenge(codeVerifier: string): Promise<string> {
  const hash = await sha256(codeVerifier)
  return base64UrlEncode(hash)
}

const PKCE_VERIFIER_KEY = 'pkce_code_verifier'
const OAUTH_STATE_KEY = 'oauth2_state'
const OAUTH_REDIRECT_KEY = 'oauth2_redirect_path'

export function storePkceVerifier(verifier: string): void {
  sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier)
}

export function retrievePkceVerifier(): string | null {
  const verifier = sessionStorage.getItem(PKCE_VERIFIER_KEY)
  sessionStorage.removeItem(PKCE_VERIFIER_KEY)
  return verifier
}

export function storeOAuthState(state: string): void {
  sessionStorage.setItem(OAUTH_STATE_KEY, state)
}

export function retrieveOAuthState(): string | null {
  const state = sessionStorage.getItem(OAUTH_STATE_KEY)
  sessionStorage.removeItem(OAUTH_STATE_KEY)
  return state
}

export function storeRedirectPath(path: string): void {
  sessionStorage.setItem(OAUTH_REDIRECT_KEY, path)
}

export function retrieveRedirectPath(): string | null {
  const path = sessionStorage.getItem(OAUTH_REDIRECT_KEY)
  sessionStorage.removeItem(OAUTH_REDIRECT_KEY)
  return path
}
